import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { ConfirmationDialog } from './ui/confirmation-dialog';
import { toast } from 'sonner';
import { useTenantSettings } from '../hooks/useTenantSettings';
import { Upload, FileText, Image, Trash2, Eye, EyeOff, Download, File, Loader2 } from 'lucide-react';

const FILE_ICONS = {
  'application/pdf': FileText,
  'image/jpeg': Image,
  'image/png': Image,
  'image/gif': Image,
  'image/webp': Image,
  'default': File,
};

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const JobFilesTab = ({ jobId, api }) => {
  const { fmtDate } = useTenantSettings();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [visibleToOperators, setVisibleToOperators] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);
  const dragCounterRef = useRef(0);

  const fetchFiles = useCallback(async () => {
    if (!jobId) return;
    try {
      const res = await api.get(`/jobs/${jobId}/files`);
      setFiles(res.data);
    } catch (err) {
      toast.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [jobId, api]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const uploadFiles = async (fileList) => {
    if (!fileList.length) return;

    setUploading(true);
    let successCount = 0;

    for (const file of fileList) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('visible_to_operators', visibleToOperators);

      try {
        await api.post(`/jobs/${jobId}/files`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        successCount++;
      } catch (err) {
        toast.error(err.response?.data?.detail || `Failed to upload ${file.name}`);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} file${successCount > 1 ? 's' : ''} uploaded`);
      await fetchFiles();
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = (e) => {
    uploadFiles(Array.from(e.target.files));
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) setDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length) uploadFiles(droppedFiles);
  };

  const handleToggleVisibility = async (fileId) => {
    try {
      const res = await api.put(`/jobs/${jobId}/files/${fileId}/visibility`);
      setFiles(prev => prev.map(f =>
        f.id === fileId ? { ...f, visible_to_operators: res.data.visible_to_operators } : f
      ));
    } catch {
      toast.error('Failed to update visibility');
    }
  };

  const handleDelete = async (fileId, filename) => {
    setDeleteTarget({ fileId, filename });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/jobs/${jobId}/files/${deleteTarget.fileId}`);
      setFiles(prev => prev.filter(f => f.id !== deleteTarget.fileId));
      toast.success('File deleted');
    } catch {
      toast.error('Failed to delete file');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const getFileIcon = (contentType) => {
    const Icon = FILE_ICONS[contentType] || FILE_ICONS['default'];
    return <Icon className="h-5 w-5 text-muted-foreground shrink-0" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
    <div className="space-y-4 py-4" data-testid="job-files-tab">
      {/* Upload area */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center space-y-3 transition-colors ${
          dragging ? 'border-[#ffd108] bg-[#ffd108]/10' : 'border-muted-foreground/25'
        }`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        data-testid="file-drop-zone"
      >
        <Upload className={`h-8 w-8 mx-auto ${dragging ? 'text-[#ffd108]' : 'text-muted-foreground'}`} />
        <div>
          <p className="text-sm font-medium">{dragging ? 'Drop files here' : 'Drag & drop files or click to upload'}</p>
          <p className="text-xs text-muted-foreground mt-1">
            PDF, images, Word, Excel, text, CSV — max 25MB per file
          </p>
        </div>
        <div className="flex items-center justify-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="visible-to-ops"
              checked={visibleToOperators}
              onCheckedChange={setVisibleToOperators}
              data-testid="visible-to-operators-toggle"
            />
            <Label htmlFor="visible-to-ops" className="text-xs cursor-pointer">
              Visible to operators
            </Label>
          </div>
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleUpload}
            data-testid="file-upload-input"
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-files-btn"
          >
            {uploading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</>
            ) : (
              <><Upload className="h-4 w-4 mr-2" />Choose Files</>
            )}
          </Button>
        </div>
      </div>

      {/* File list */}
      {files.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4">No files uploaded yet</p>
      ) : (
        <div className="space-y-2">
          <p className="text-sm font-medium text-muted-foreground">{files.length} file{files.length !== 1 ? 's' : ''}</p>
          {files.map(file => (
            <div
              key={file.id}
              className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors group"
              data-testid={`job-file-${file.id}`}
            >
              {getFileIcon(file.content_type)}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{file.filename}</p>
                <p className="text-xs text-muted-foreground">
                  {formatFileSize(file.size_bytes)} · {file.uploaded_by_name} · {fmtDate(file.uploaded_at)}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {file.visible_to_operators ? (
                  <Badge variant="outline" className="text-xs gap-1 border-green-500/30 text-green-600">
                    <Eye className="h-3 w-3" /> Operator
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs gap-1 text-muted-foreground">
                    <EyeOff className="h-3 w-3" /> Hidden
                  </Badge>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => handleToggleVisibility(file.id)}
                  title={file.visible_to_operators ? 'Hide from operators' : 'Show to operators'}
                  data-testid={`toggle-visibility-${file.id}`}
                >
                  {file.visible_to_operators ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => window.open(file.url, '_blank')}
                  title="Download"
                  data-testid={`download-file-${file.id}`}
                >
                  <Download className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  onClick={() => handleDelete(file.id, file.filename)}
                  title="Delete"
                  data-testid={`delete-file-${file.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>

    <ConfirmationDialog
      open={!!deleteTarget}
      onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
      title="Delete File"
      description={`Are you sure you want to delete "${deleteTarget?.filename}"? This cannot be undone.`}
      confirmText="Delete"
      variant="destructive"
      onConfirm={confirmDelete}
      isLoading={deleting}
      testId="delete-job-file-dialog"
    />
    </>
  );
};
